import type { ApplicationContext, ErrorCode } from '@hc-frontend/core-context';
import type { JourneyAlias } from '@hc-frontend/core-types';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { keys, max, reduce } from 'ramda';

import type { UserJourney } from '../../entities/user-journey';
import type { JourneyRepository } from '../../repositories';

type GetJourneyProgressOptions = {
  journeyAlias: JourneyAlias;
};

export type GetJourneyFactoryParams = Pick<
  JourneyRepository,
  'getJourney' | 'getCurrentPlate'
> &
  Pick<ApplicationContext, 'log'>;

export type GetJourneyProgressError =
  ErrorCode<'failedToHandleContextualTraffic'>;

export type GetJourneyProgressUseCase = (
  options: GetJourneyProgressOptions,
) => Promise<IOperationResult<UserJourney, GetJourneyProgressError>>; // Let's make this use case not to fail and return a number

export function getJourneyProgressFactory({
  log,
  getJourney,
  getCurrentPlate,
}: GetJourneyFactoryParams): GetJourneyProgressUseCase {
  return async ({ journeyAlias }) => {
    const result = await getJourney(journeyAlias);

    if (!result.success) {
      log({
        type: 'error',
        context: 'server',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToHandleContextualTraffic',
            rootCause: result.errors[0].rootCause,
          },
        ],
      });
    }

    const { plates, id } = result.data;

    const findMaxOrder = reduce(
      (acc, current) => max(acc, plates[current]?.order ?? 0),
      0,
      keys(plates),
    );

    const currentPlate = await getCurrentPlate();

    if (!currentPlate.success) {
      log({
        type: 'error',
        context: 'server',
        message: currentPlate.errors[0].code,
        data: {
          rootCause: currentPlate.errors[0].rootCause,
        },
      });
      return createSuccessOperationResult({
        progress: 0,
        id,
        currentPlateOrder: 0,
      });
    }

    const currentPlateOrder = plates[currentPlate.data]?.order ?? 0;

    return createSuccessOperationResult({
      progress: (currentPlateOrder / findMaxOrder) * 100,
      id,
      currentPlateOrder,
    });
  };
}
